const _ = require("lodash");
const Big = require("big.js");

const cleanHTML = function(text) {
	//Escaping a lot just to be safe since people can use different character sets
	const replaceWith = {
		"&": "&amp;",
		"<": "&lt;",
		">": "&gt;",
		'"': "&quot;",
		"'": "&#39;",
		"/": "&#x2F;",
		"`": "&#x60;",
		"=": "&#x3D;",
		"!": "&#33;",
		"@": "&#64;",
		$: "&#36;",
		"%": "&#37;",
		"(": "&#40;",
		")": "&#41;",
		"{": "&#123;",
		"}": "&#125;",
		"[": "&#91;",
		"]": "&#93;",
		"+": "&#43;",
		"-": "&#45;",
	};

	return String(text).replace(/[&<>"'`=\/\!@$%(){}\[\]+-]/g, s => {
		return replaceWith[s];
	});
};

// Escaping a string for regex is not in Javascript. Below is from:
// http://stackoverflow.com/questions/3561493/is-there-a-regexp-escape-function-in-javascript/
const escapeRegex = function(s) {
	return s.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
};

const getPluralString = function(value, type) {
	let pluralString;

	if (value === 1) {
		pluralString = "1 " + type;
	} else {
		pluralString = value + " " + type + "s";
	}

	return pluralString;
};

//Capitalizes every word in the given string
const getAllWordsCapitalized = function(string) {
	return string
		.split(" ")
		.map(_.capitalize)
		.join(" ");
};

const allWordsLowerCase = function(string) {
	const spacedCaps = string
		.split(" ")
		.map(substring => _.lowerFirst(substring))
		.join(" ");
	return spacedCaps
		.split("-")
		.map(substring => _.lowerFirst(substring))
		.join("-");
};

const getUsernamePieces = function(username) {
	const usernamePieces = username.split(/(?=[A-Z])|(?=\d)/g);
	const adjective = usernamePieces[0] || "";
	const animal = usernamePieces[1] || "";
	const number = usernamePieces.slice(2).join("") || "";

	return {adjective, animal, number};
};

// Matches default cart subscription placeholders from StoreUtil.getSubscriptionPlaceholderName
// ("Student A", "Student B", …); same pattern as isStudentLetterDisplayName in Cart.jsx.
const isSubscriptionPlaceholderDisplayName = function(name) {
	return typeof name === "string" && /^Student [A-Z]+$/i.test(name.trim());
};

// Return a human-readable string of the names in the array
const combineNames = function(nameArray) {
	const num = nameArray.length;
	return (
		_.dropRight(nameArray, 1).join(", ") +
		(num > 2 ? "," : "") +
		(num > 1 ? " and " : "") +
		_.last(nameArray)
	);
};

// Return a human-readable string of the names in the array, stripping placeholder names
const prepPlaceholderNamesForEmail = function(nameArray) {
	if (!nameArray.length) {
		return "your student";
	}
	if (
		nameArray.length === 1 &&
		isSubscriptionPlaceholderDisplayName(nameArray[0])
	) {
		return "your student";
	}
	if (
		nameArray.length > 1 &&
		_.some(nameArray, isSubscriptionPlaceholderDisplayName)
	) {
		return "your students";
	}
	return combineNames(nameArray);
};

// Greeting name for order emails: account firstName when set, else first token
// of billing/shipping name.
const getEmailFirstName = function(accountFirstName, billingName) {
	const fromAccount = accountFirstName?.trim() || "";
	if (fromAccount) return fromAccount;
	return billingName?.trim().split(/\s+/)[0] || "";
};

// Duplicated in src/elev/_assets/js/ElevHelpers.js
const isValidEmail = function(email) {
	return email && /^[A-Z0-9._%+\-]+@([A-Z0-9\-]+\.)+[A-Z]{2,}$/i.test(email);
};

const isZipcodeCharacters = function(zipcode) {
	return zipcode && /^[A-Za-z0-9\- ]+$/.test(zipcode);
};

//Works on strings, Bigs, and floats
// dec, defaulting to 2, is number of decimal places displayed
const toPrice = function(price, dec = 2) {
	//Always use 2 decimal places if the number has a decimal.
	if (
		!Big(price)
			.round()
			.eq(Big(price))
	) {
		dec = 2;
	}
	const priceString = typeof price === "string" ? price : price.toFixed(dec);
	if (priceString.charAt(0) === "-") {
		return "-$" + priceString.slice(1);
	} else {
		return "$" + priceString;
	}
};

const toShortPrice = function(price) {
	return toPrice(price, 0);
};

const priceToBig = function(priceString) {
	const num = priceString.slice(1);
	return Big(num);
};

const toTimeString = function(inMinutes, short) {
	const hours = Math.floor(inMinutes / 60);
	const mins = Math.ceil(inMinutes % 60);

	let durationString = "";

	if (hours > 0) {
		durationString += short ? hours + "h " : getPluralString(hours, "hr") + " ";
	}

	if (hours < 1000) {
		durationString += mins + (short ? "m" : " min");
	}

	return durationString;
};

const isASCII = function(str) {
	return /^[\x20-\x7F]*$/.test(str);
};

const isValidCheckoutString = function(str) {
	// Allow all ASCII characters, ł, or ń
	// A customer is trying to place an order with
	// billing address that has a ł and ń in it.
	// This is a quick hacky fix.
	return /^[\x20-\x7Fłń]*$/.test(str);
};

// Returns whether the passed argument is already an array of nonnegative
// integers or, if a string, a comma-separated list of nonnegative integers.
const isValidNumberList = function(x) {
	if (Array.isArray(x)) {
		return x.every(p => _.isFinite(p) && p >= 0);
	} else if (typeof x !== "string") {
		return false;
	}
	let parts = x.split(",");
	// Terminating commas are okay.
	if (_.last(parts) === "") {
		parts = parts.slice(0, -1);
	}
	return parts.every(p => /^[0-9]+$/.test(p.trim()));
};

// Convert comma separated list of numbers to array of numbers. Assumes
// either that isValidNumberList returns true for this input, or that the input
// is falsy (in which case null is returned).
const convertNumberList = function(x) {
	if (!x) {
		return null;
	} else if (Array.isArray(x)) {
		return x;
	}
	return x.split(",").map(p => parseInt(p.trim()));
};

// Returns whether the passed string is a valid price.
const isValidPrice = function(x) {
	try {
		Big(x).toFixed(2);
	} catch (ex) {
		return false;
	}
	return true;
};

// Convert the passed string to a valid price. Assumes
// either that isValidNumberList returns true for this input, or that the input
// is falsy (in which case null is returned).
const convertPrice = function(x) {
	if (!x) {
		return null;
	}
	return Big(x).toFixed(2);
};

// Converts a string to a series of character codes numbers, joined by a period between each code.
const convertToCharCode = function(str) {
	const codesList = _.map(str, (s, i) => str.charCodeAt(i));
	return codesList.join(".");
};

// Converts from a series of character codes to a string.
// Character codes are in string format separated by a period between each new character code. e.g. "123.34.45"
const convertFromCharCode = function(charCodes) {
	const codesList = charCodes.split(".");
	return String.fromCharCode(...codesList);
};

// Gets the first letter of each word as abbreviation
const getAcronym = function(str) {
	const words = _.words(str);
	_.pull(words, "the", "a", "The", "A");
	return _.toUpper(_.map(words, w => w.trim()[0]).join(""));
};

// csvRows is an array of rows, where each row
// is an array of strings that represent the values in
// a CSV row. This function converts this 2-d array into
// a CSV string, escaping commas and double quotes as necessary.
const convertCSVArrayToText = csvRows => {
	return _.join(
		_.map(csvRows, row => {
			// CSV escape the rows
			return _.join(
				_.map(row, val => {
					const needsToBeEscaped =
						_.isString(val) && (_.includes(val, ",") || _.includes(val, '"'));
					return needsToBeEscaped ? `"${val.replace('"', '""')}"` : val;
				})
			);
		}),
		"\n"
	);
};

// Wraps the string in cleanHTML and replaces newlines with <br>
// Can be useful for formating text for an email body.
const formatEmailBody = function(str) {
	return cleanHTML(str).replace(/[ \t]*\n[ \t]*/g, " <br>\n");
};

const pascalCase = function(str) {
	return _.startCase(_.camelCase(str)).replace(/ /g, "");
};

// Displays an object's keys and values in a string, each on a new line
const displayFormattedObject = fields =>
	Object.entries(fields)
		.map(([key, val]) => `${key}:  ${val}`)
		.join("\n");

// Builds string of cookie-encoded key value pairs from an array of key, value, key, value, ...
/**
 * Turn an array of cookie keys and values into a string to be stored as a cookie
 * @param {Array} alternatingKeysAndValuesArray array of arrays with cookie values
 * @param {string} keyValueSeparator string to separate a dictionary key and value
 * @param {string} entrySeparator string to separate entry values
 * @returns headers object with cookies set
 */
const makeDictionaryStringFromAlternatingKeyValueArray = (
	alternatingKeysAndValuesArray = [],
	keyValueSeparator = "=",
	entrySeparator = "; "
) => {
	const asKeyValuePairs = _.chunk(alternatingKeysAndValuesArray, 2);
	const res = asKeyValuePairs
		.map(entry => entry.join(keyValueSeparator))
		.join(entrySeparator);
	return res;
};

const validBoolStringEntriesForTrue = ["t", "true", "y", "yes"];

// Takes in a string that is assumed to be a boolean and returns "true" if
// the trimmed, case-insensitive value is in the list of approved values above.
// Otherwise returns "".
// Used because we generally use string inputs in BAEditor for booleans
// because they can be left empty and can be added after an engine already exists.
const ensureBoolStringConvention = function(potentialBool) {
	if (potentialBool) {
		const cleanLowerCaseValue = _.clone(potentialBool)
			.toString()
			.trim()
			.toLowerCase();
		if (validBoolStringEntriesForTrue.includes(cleanLowerCaseValue)) {
			return "true";
		}
	}
	return "";
};

module.exports = {
	cleanHTML,
	escapeRegex,
	getPluralString,
	getAllWordsCapitalized,
	allWordsLowerCase,
	getUsernamePieces,
	combineNames,
	isSubscriptionPlaceholderDisplayName,
	prepPlaceholderNamesForEmail,
	getEmailFirstName,
	isValidEmail,
	isZipcodeCharacters,
	toPrice,
	toShortPrice,
	priceToBig,
	toTimeString,
	isASCII,
	isValidCheckoutString,
	isValidNumberList,
	convertNumberList,
	isValidPrice,
	convertPrice,
	convertToCharCode,
	convertFromCharCode,
	getAcronym,
	convertCSVArrayToText,
	formatEmailBody,
	pascalCase,
	displayFormattedObject,
	makeDictionaryStringFromAlternatingKeyValueArray,
	ensureBoolStringConvention,
};
